import React, { useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { Validation } from "../../../../domain/validation/Validation";
import { Card, Stack } from "@mui/material";
import {
  STRINGS_EMAIL,
  STRINGS_EMAIL_NOT_VALID,
  STRINGS_EMAIL_REQUIRED,
  STRINGS_LOGIN,
  STRINGS_PASSWORD,
  STRINGS_PASSWORD_REQUIRED,
} from "../../../../data/strings/Strings";
import {
  COLOR_BLACK,
  COLOR_LESS_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_VERY_LIGHT_GRAY,
  COLOR_WHITE,
} from "../../../../data/colors/Colors";
import useWindowDimensions from "../../../../core/utils/UseWindowDimensions";
import AutoHeight from "../../../component/AutoHeightView";
import { EmailRounded, LockRounded, PersonRounded } from "@mui/icons-material";
import { CustomTextView } from "../../../component/CustomTextView";
import { CustomTextField } from "../../../component/CustomTextField";
import { CustomButtonView } from "../../../component/CustomButtonView";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ROUTE_ENDPOINTS } from "../../../../data/constant/RouteConstants";
import PhoneInput, { CountryData } from "react-phone-input-2";
import ar from "react-phone-input-2/lang/ar.json";
import "react-phone-input-2/lib/style.css";
import { MAIN_FONT_FAMILY } from "../../../../data/constant/Constants";
import { SignupRequestModel } from "../../../../domain/model/Auth";
import OtpInput from "react-otp-input";
import { enqueueSnackbar, useSnackbar } from "notistack";

interface SignupFormProps {
  onSubmit: (signupRequestModel: SignupRequestModel) => void;
  onSendVerificationCode: (phoneNumber: string) => Promise<boolean>;
  onCheckVerificationCode: (
    phoneNumber: string,
    code: string
  ) => Promise<boolean>;
}

function SignupForm({
  onSubmit,
  onSendVerificationCode,
  onCheckVerificationCode,
}: SignupFormProps) {
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [phoneNumber, setPhoneNumber] = useState("974");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const { enqueueSnackbar } = useSnackbar();

  const [phoneNumberCountryData, setPhoneNumberCountryData] = useState<
    CountryData | undefined
  >();

  const handlePhoneChange = (
    value: string,
    data: CountryData | {},
    _: React.ChangeEvent<HTMLInputElement>,
    formattedValue: string
  ) => {
    setPhoneNumber(value);

    console.log(value);
    console.log(data as CountryData);
    console.log(formattedValue);

    const countryData = data as CountryData;
    setPhoneNumberCountryData(countryData);
  };

  const handleNameChange = (name: string) => {
    setName(name);
  };

  const handleEmailChange = (email: string) => {
    setEmail(email);
  };

  const handlePasswordChange = (password: string) => {
    setPassword(password);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const errors = validateInputs();
    setErrors(errors);

    if (Object.keys(errors).length === 0 && phoneNumberCountryData) {
      if (signupProcess == 1) {
        const result = await onSendVerificationCode(`+${phoneNumber}`);
        if (!result) return;
        setSignupProcess(2);
      } else if (signupProcess == 2) {
        if (otp.length != 6) {
          enqueueSnackbar("OTP", { variant: "error" });
          return;
        }
        const result = await onCheckVerificationCode(`+${phoneNumber}`, otp);
        if (!result) return;
        setSignupProcess(3);
      } else {
        const model: SignupRequestModel = {
          email,
          password,
          name,
          role: "player",
          phoneNumber: {
            number: phoneNumber.replace(phoneNumberCountryData.dialCode, ""),
            countryCode: `+${phoneNumberCountryData.dialCode}`,
            countryISOCode: phoneNumberCountryData.countryCode.toUpperCase(),
          },
        };
        onSubmit(model);
      }
    }
  };

  const validateInputs = () => {
    const errors: { [key: string]: string } = {};
    const emailValid = Validation.validateEmail(email);
    const phoneNumberValid = Validation.validatePhoneNumber(phoneNumber);

    if (phoneNumber.length <= phoneNumberCountryData?.dialCode.length!) {
      errors.phoneNumber = t("please_enter_field", {
        field: t("phone_number"),
      });
    } else if (phoneNumberValid !== true) {
      errors.phoneNumber = t("phone_number_invalid");
    }
    if (signupProcess == 1 || signupProcess == 2) return errors;
    if (name.length === 0) {
      errors.name = t("please_enter_field", { field: t("name") });
    }
    if (emailValid !== true) {
      errors.email = t("email_not_valid");
    }
    const passwordValid = Validation.validateStrongPassword(password);
    if (passwordValid !== true) {
      errors.password = t("password_not_strong");
    }
    if (email.length === 0) {
      errors.email = t("please_enter_field", { field: t("email") });
    }
    if (password.length === 0) {
      errors.password = t("please_enter_field", { field: t("password") });
    }
    return errors;
  };

  const { width } = useWindowDimensions();
  const isMobile = width < 700;

  const { t, i18n } = useTranslation();

  const navigate = useNavigate();

  const ref = useRef(null);
  const [inputHeight, setInputHeight] = useState(0);
  useEffect(() => {
    setInputHeight((ref.current as any)?.offsetHeight);
  }, [ref.current]);

  const [isFocused, setIsFocused] = useState(false);

  const [signupProcess, setSignupProcess] = useState(1);
  const [otp, setOtp] = useState("");

  const handleOnFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Card
        elevation={0}
        sx={{
          flex: 1,
          padding: "1.5rem",
          margin: "1.5rem",
          borderRadius: 8,
          minWidth: "25vw",
          width: "100%",
          backgroundColor: "white",
          minHeight: "75vh",
          display: "flex",
          alignItems: "center",
          background: COLOR_VERY_LIGHT_GRAY,
        }}
      >
        <Stack spacing={8} flex={1}>
          <CustomTextView
            text={t("signup")}
            variant={width < 700 ? "h3" : "h4"}
            fontWeight="bold"
            textColor={COLOR_MAIN}
          />

          <Stack spacing={1}>
            {signupProcess == 1 && (
              <Stack sx={{ p: 1 }}>
                <PhoneInput
                  country={"qa"}
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  localization={i18n.language == "ar" ? ar : undefined}
                  inputProps={{
                    name: t("phone_number"),
                    required: true,
                  }}
                  onEnterKeyPress={(e) => {
                    e.preventDefault();
                  }}
                  onMount={(_, data: any) => {
                    setPhoneNumberCountryData(data as CountryData);
                  }}
                  onFocus={handleOnFocus}
                  onBlur={handleBlur}
                  placeholder={t("phone_number")}
                  inputStyle={{
                    borderRadius: 40,
                    fontSize: 16,
                    width: "100%",
                    height: "100%",
                    fontFamily: MAIN_FONT_FAMILY,
                    borderColor: errors.phoneNumber
                      ? "#d3302f"
                      : isFocused
                      ? COLOR_LIGHT_GRAY
                      : COLOR_MAIN,
                    borderWidth: isFocused ? 2 : undefined,
                    direction: "ltr",
                    background: "transparent",
                  }}
                  buttonStyle={{
                    borderRadius: 40,
                    alignSelf: "center",
                    marginLeft: 10,
                    borderWidth: 0,
                    direction: "ltr",
                  }}
                  containerStyle={{
                    borderRadius: 40,
                    height: inputHeight ? inputHeight : 50,
                    direction: "ltr",
                  }}
                  dropdownStyle={{
                    borderRadius: 12,
                    fontFamily: MAIN_FONT_FAMILY,
                    direction: i18n.dir(),
                  }}
                />
                {errors.phoneNumber && (
                  <CustomTextView
                    text={errors.phoneNumber}
                    fontSize={12}
                    textColor={"#d3302f"}
                    props={{
                      marginLeft: i18n.dir() == "rtl" ? 0 : 1.8,
                      marginTop: 1,
                      marginRight: i18n.dir() == "ltr" ? 0 : 1.8,
                    }}
                  />
                )}
              </Stack>
            )}

            {signupProcess == 2 && (
              <Stack width={"100%"} alignItems={"center"}>
                <OtpInput
                  value={otp}
                  onChange={setOtp}
                  numInputs={6}
                  containerStyle={{
                    direction: "ltr",
                  }}
                  renderSeparator={<Box p={0.5} />}
                  renderInput={(props) => <input {...props} />}
                  shouldAutoFocus
                  inputType="tel"
                  inputStyle={{
                    width: isMobile ? 40 : 50,
                    height: isMobile ? 60 : 70,
                    borderRadius: 10,
                    fontFamily: MAIN_FONT_FAMILY,
                    fontSize: 20,
                  }}
                />
              </Stack>
            )}

            {signupProcess == 3 && (
              <Stack spacing={1}>
                <AutoHeight>
                  <Box sx={{ p: 1 }}>
                    <CustomTextField
                      type="text"
                      placeholder={t("name")}
                      isFullWidth
                      onTextChange={handleNameChange}
                      error={!!errors.name}
                      helperText={errors.name}
                      startIcon={<PersonRounded sx={{ color: COLOR_MAIN }} />}
                      textColor={COLOR_BLACK}
                      required
                      onHoverBorderColor={COLOR_LIGHT_GRAY}
                      onFocusBorderColor={COLOR_LIGHT_GRAY}
                      borderColor={COLOR_MAIN}
                      backgroundColor={"white"}
                    />
                  </Box>
                </AutoHeight>
                <AutoHeight>
                  <Box sx={{ p: 1 }}>
                    <Box ref={ref}>
                      <CustomTextField
                        type="email"
                        placeholder={t("email")}
                        isFullWidth
                        onTextChange={handleEmailChange}
                        error={!!errors.email}
                        helperText={errors.email}
                        startIcon={<EmailRounded sx={{ color: COLOR_MAIN }} />}
                        textColor={COLOR_BLACK}
                        required
                        onHoverBorderColor={COLOR_LIGHT_GRAY}
                        borderColor={COLOR_MAIN}
                        onFocusBorderColor={COLOR_LIGHT_GRAY}
                        backgroundColor={"white"}
                      />
                    </Box>
                  </Box>
                </AutoHeight>
                <AutoHeight>
                  <Box sx={{ p: 1 }}>
                    <CustomTextField
                      type="password"
                      placeholder={t("password")}
                      isFullWidth
                      onTextChange={handlePasswordChange}
                      error={!!errors.password}
                      helperText={errors.password}
                      required
                      startIcon={<LockRounded sx={{ color: COLOR_MAIN }} />}
                      textColor={COLOR_BLACK}
                      onHoverBorderColor={COLOR_LIGHT_GRAY}
                      onFocusBorderColor={COLOR_LIGHT_GRAY}
                      borderColor={COLOR_MAIN}
                      backgroundColor={"white"}
                    />
                  </Box>
                </AutoHeight>
              </Stack>
            )}

            <Stack width={"100%"} p={1}>
              <CustomButtonView
                text={t("signup")}
                type="submit"
                props={{ padding: 1 }}
              />
            </Stack>

            <Stack
              direction={"row"}
              alignItems={"center"}
              width={"100%"}
              justifyContent={"center"}
              paddingTop={2}
            >
              <CustomTextView
                text={t("already_have_account")}
                textColor={COLOR_LIGHT_GRAY}
                variant="body2"
              />

              <Button
                sx={{ borderRadius: 12 }}
                onClick={() => {
                  navigate(ROUTE_ENDPOINTS.LOGIN);
                }}
              >
                <CustomTextView
                  text={t("login")}
                  textColor={COLOR_MAIN}
                  variant="body1"
                  props={{ textTransform: "none" }}
                  fontWeight="bold"
                />
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Card>
    </Box>
  );
}

export default SignupForm;
